import { useEffect, useMemo, useState } from "react";
import { StatusBlock } from "../components/StatusBlock";
import { CompanyBrowserModal } from "../company-browser/CompanyBrowserModal";
import type { CompanyBrowserTarget } from "../company-browser/types";
import type { LoadState, PresenceRecord } from "../types";
import { loadTeamActivity, type TeamActivityAccount, type TeamActivityPayload, type TeamTimelineItem } from "./loadTeamActivity";
import { relativeTime } from "./TeamBrowser";
import { memberHasGps, memberIsOnline, teamMemberFromPresence, tileColor, type TeamMember } from "./teamColors";

export function TeamActivityModal({
  member,
  presence,
  eventKey,
  campaignKey,
  selfEmail,
  selfUserId,
  selfGpsOn,
  selfOnline,
  onClose,
  onOpenAccount,
}: {
  member: TeamMember;
  presence: PresenceRecord[];
  eventKey: string;
  campaignKey: string;
  selfEmail?: string;
  selfUserId?: string;
  selfGpsOn: boolean;
  selfOnline: boolean;
  onClose: () => void;
  onOpenAccount: (target: CompanyBrowserTarget) => void;
}) {
  const [view, setView] = useState<"timeline" | "accounts">("timeline");
  const [state, setState] = useState<LoadState<TeamActivityPayload>>({ status: "loading" });
  const gpsOn = memberHasGps(member, presence, { selfEmail, selfUserId, selfGpsOn });
  const online = memberIsOnline(member, presence, { selfEmail, selfUserId, selfOnline });
  const color = tileColor(member, gpsOn);
  const checkIns = presence.find((entry) => teamMemberFromPresence(entry)?.email === member.email)?.check_ins;

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    void loadTeamActivity({ eventKey, campaignKey, userKey: member.email })
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: error instanceof Error ? error.message : "Could not load activity",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [campaignKey, eventKey, member.email]);

  const payload = useMemo(() => {
    if (state.status !== "ready") return null;
    return {
      ...state.data,
      timeline: mergeCheckIns(state.data.timeline, checkIns),
    };
  }, [checkIns, state]);

  return (
    <CompanyBrowserModal title={`${member.name} activity`} onClose={onClose} className="team-activity-modal">
      <header className="company-modal-header team-activity-header">
        <div className="company-modal-heading">
          <div className="team-activity-identity">
            <span
              className={`team-tile${gpsOn ? "" : " gps-off"}`}
              style={{ background: color.fill, borderColor: color.stroke }}
            >
              {member.initials}
            </span>
            <div>
              <h2>{member.name}</h2>
              <p className="muted">{[online ? "Online" : "Offline", gpsOn ? "GPS on" : "GPS off"].join(" · ")}</p>
            </div>
          </div>
          <button type="button" className="secondary" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="team-activity-toggle" role="tablist" aria-label="Activity views">
          <button
            type="button"
            role="tab"
            className={view === "timeline" ? "tab on" : "tab"}
            aria-selected={view === "timeline"}
            onClick={() => setView("timeline")}
          >
            Timeline
          </button>
          <button
            type="button"
            role="tab"
            className={view === "accounts" ? "tab on" : "tab"}
            aria-selected={view === "accounts"}
            onClick={() => setView("accounts")}
          >
            Accounts
          </button>
        </div>
      </header>
      <div className="company-modal-body">
        {payload ? (
          view === "timeline" ? (
            <TimelineList rows={payload.timeline} onOpenAccount={onOpenAccount} />
          ) : (
            <AccountList rows={payload.accounts} onOpenAccount={onOpenAccount} />
          )
        ) : (
          <StatusBlock state={state} loadingLabel="Loading activity…" />
        )}
      </div>
    </CompanyBrowserModal>
  );
}

function mergeCheckIns(timeline: TeamTimelineItem[], checkIns: PresenceRecord["check_ins"]): TeamTimelineItem[] {
  const extra: TeamTimelineItem[] = (checkIns ?? []).map((entry) => ({
    kind: "checkin",
    id: `checkin:${entry.booth_id}:${entry.at}`,
    at: entry.at,
    title: entry.booth_label ? `${entry.booth_label} ${entry.booth_id}` : `Booth ${entry.booth_id}`,
    subtitle: null,
    booth: entry.booth_id,
  }));
  const withoutCheckins = timeline.filter((row) => row.kind !== "checkin");
  return [...withoutCheckins, ...extra].sort((a, b) => b.at.localeCompare(a.at) || a.id.localeCompare(b.id));
}

function TimelineList({
  rows,
  onOpenAccount,
}: {
  rows: TeamTimelineItem[];
  onOpenAccount: (target: CompanyBrowserTarget) => void;
}) {
  if (!rows.length) return <p className="muted">No check-ins, contacts, or notes yet.</p>;
  return (
    <ul className="team-activity-list">
      {rows.map((row) => {
        const open = row.account_id
          ? () => onOpenAccount({ account_id: row.account_id ?? undefined, booth_number: row.booth ?? undefined })
          : undefined;
        return (
          <li key={`${row.kind}:${row.id}`}>
            {open ? (
              <button type="button" className="team-activity-row" onClick={open}>
                <TimelineBody row={row} />
              </button>
            ) : (
              <div className="team-activity-row static">
                <TimelineBody row={row} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function TimelineBody({ row }: { row: TeamTimelineItem }) {
  return (
    <>
      <span className={`team-activity-kind ${row.kind}`} aria-hidden="true">
        {row.kind === "checkin" ? "In" : row.kind === "contact" ? "Co" : "No"}
      </span>
      <span className="team-activity-copy">
        <strong>{row.title}</strong>
        <span className="muted">
          {[kindLabel(row.kind), row.subtitle, relativeTime(row.at)].filter(Boolean).join(" · ")}
        </span>
      </span>
    </>
  );
}

function AccountList({
  rows,
  onOpenAccount,
}: {
  rows: TeamActivityAccount[];
  onOpenAccount: (target: CompanyBrowserTarget) => void;
}) {
  if (!rows.length) return <p className="muted">No owned, favorited, or closed-out accounts yet.</p>;
  return (
    <ul className="team-activity-list">
      {rows.map((row) => (
        <li key={row.account_id}>
          <button
            type="button"
            className="team-activity-row"
            onClick={() => onOpenAccount({ account_id: row.account_id, booth_number: row.booth ?? undefined })}
          >
            <span className="team-activity-copy">
              <strong>{row.name || "Account"}</strong>
              <span className="muted">{row.booth ? `Booth ${row.booth}` : "No booth"}</span>
            </span>
            <span className="team-activity-flags">
              {row.my_favorite ? (
                <span className="team-flag fav" title="Favorited">
                  Fav
                </span>
              ) : null}
              {row.ai_rating != null && row.ai_rating >= 4 ? (
                <span className="team-flag rating" title={`AI ${row.ai_rating}`}>
                  {row.ai_rating}★
                </span>
              ) : null}
              <span className={`team-flag status${row.closed_out ? " closed" : ""}`}>
                {row.closed_out ? "Closed" : "Open"}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function kindLabel(kind: TeamTimelineItem["kind"]): string {
  if (kind === "checkin") return "Check-in";
  if (kind === "contact") return "Contact";
  return "Note";
}
